<template>
  <div v-if="allowRecording && !previewModalOpen" class="record-slider-wrapper">
    <div v-if="isVsMode && vsPairs.length" class="record-slider record-slider-vs">
      <button class="nav-arrow" @click="prevVsPage" :disabled="!hasPrevVsPage">
        <i class="fa-solid fa-chevron-left"></i>
      </button>

      <div class="record-cards record-cards-vs">
        <div
          v-for="pair in visibleVsPairs"
          :key="pair.key"
          :class="['record-card', 'pair-card', { 'is-recording': isPairRecording(pair) }]"
        >
          <div class="vs-row">
            <div class="vs-athlete">
              <img :src="pair.left?.user?.img_path || defaultAvatar" alt="athlete" class="record-avatar" />
              <div class="record-name mini">
                <span class="record-line">{{ pair.leftName }}</span>
              </div>
            </div>
            <div class="vs-separator">VS</div>
            <div class="vs-athlete">
              <img :src="pair.right?.user?.img_path || defaultAvatar" alt="athlete" class="record-avatar" />
              <div class="record-name mini">
                <span class="record-line">{{ pair.rightName }}</span>
              </div>
            </div>
          </div>

          <div class="vs-actions">
            <button
                v-if="!isPairRecording(pair) && !shouldShowPairUpload(pair) && !recordingLocked"
              class="btn btn-sm btn_actions color-gray"
              :disabled="vsStartDisabled(pair) || hasRunningTimer"
              @click.stop="handleVsStart(pair)"
              :title="!cameraReady ? $t('judge.camera.start_required') : $t('judge.start_recording')"
            >
              <i class="fa-solid fa-eye text-primary"></i>
            </button>
            <button
              v-else-if="isPairRecording(pair)"
              class="btn btn-sm btn_actions color-gray"
              :disabled="hasRunningTimer || true"
              :title="$t('judge.stop_recording')"
            >
              <i class="fa-solid fa-eye text-primary"></i>
            </button>
            <div class="vs-upload">
              <template v-if="shouldRenderProgress(getPairUploadProgress(pair), pair)">
                <span
                  class="progress-circle"
                  :class="{ 'progress-circle--done': isProgressComplete(getPairUploadProgress(pair)) }"
                >
                  {{ getPairUploadProgress(pair).percent }}%
                </span>
                <button
                  v-if="canMarkProcessing(getPairUploadProgress(pair), pair)"
                  class="btn btn-sm btn_actions upload-btn status21-btn"
                  @click.stop="markPairProcessing(pair)"
                  :title="$t('judge.status_21') || 'Subido'"
                >
                  <i class="fa-solid fa-circle-check"></i>
                </button>
              </template>
              <button
                  v-else-if="!isPairRecording(pair) && shouldShowPairUpload(pair) && !recordingLocked"
                class="btn btn-sm btn_actions upload-btn"
                :disabled="uploadActionsLocked || recordingLocked"
                @click.stop="handleVsUpload(pair)"
                :title="$t('judge.upload') || 'Subir video'"
              >
                <i class="fa-solid fa-cloud-arrow-up text-primary"></i>
              </button>
            </div>
            <button
              v-if="shouldShowPairDelete(pair)"
              class="btn btn-sm btn_actions delete-btn"
              :disabled="uploadActionsLocked || recordingLocked || isUploadingRecorded"
              @click.stop="handleVsDelete(pair)"
              :title="$t('judge.delete') || 'Eliminar video'"
            >
              <i class="fa-solid fa-trash"></i>
            </button>
          </div>
          <div
            v-if="shouldShowUploadFeedback(getPairUploadProgress(pair))"
            :class="uploadFeedbackClass(getPairUploadProgress(pair))"
          >
            {{ uploadFeedbackText(getPairUploadProgress(pair)) }}
          </div>
        </div>
      </div>

      <button class="nav-arrow" @click="nextVsPage" :disabled="!hasNextVsPage">
        <i class="fa-solid fa-chevron-right"></i>
      </button>
    </div>

    <div v-else-if="sliderAthletes.length" class="record-slider">
      <button class="nav-arrow" @click="prevPage" :disabled="!hasPrevPage">
        <i class="fa-solid fa-chevron-left"></i>
      </button>

      <div class="record-cards">
        <div
            v-for="athlete in visibleAthletes"
            :key="athlete.id"
            :class="['record-card', { active: athlete.id === currentAthlete?.id, 'is-recording': athlete.status_id === 16 } ]"
            @click="selectAthlete(athlete)"
        >
          <img
              :src="athlete?.user?.img_path || defaultAvatar"
              alt="athlete"
              class="record-avatar"
          />
          <div class="record-name mini">
            <span class="record-line">{{ [athlete?.user?.name, athlete?.user?.surname].filter(Boolean).join(' ') }}</span>
          </div>
          <div class="record-actions compact">
            <template v-if="shouldShowUpload(athlete)">
              <template v-if="shouldRenderProgress(getUploadProgress(athlete), athlete)">
                <span
                  class="progress-circle"
                  :class="{ 'progress-circle--done': isProgressComplete(getUploadProgress(athlete)) }"
                >
                  {{ getUploadProgress(athlete).percent }}%
                </span>
                <button
                  v-if="canMarkProcessing(getUploadProgress(athlete), athlete)"
                  class="btn btn-sm btn_actions upload-btn status21-btn"
                  @click.stop="markProcessing(athlete)"
                  :title="$t('judge.status_21') || 'Subido'"
                >
                  <i class="fa-solid fa-circle-check"></i>
                </button>
              </template>
              <button
                  v-else
                  class="btn btn-sm btn_actions upload-btn"
                  :disabled="uploadActionsLocked || recordingLocked"
                  @click.stop="$emit('upload-from-card', athlete)"
                  :title="$t('judge.upload') || 'Subir video'"
              >
                <i class="fa-solid fa-cloud-arrow-up text-primary"></i>
              </button>
            </template>
            <button
              v-if="shouldShowDelete(athlete)"
              class="btn btn-sm btn_actions delete-btn"
              :disabled="uploadActionsLocked || recordingLocked || isUploadingRecorded"
              @click.stop="$emit('delete-from-card', athlete)"
              :title="$t('judge.delete') || 'Eliminar video'"
            >
              <i class="fa-solid fa-trash"></i>
            </button>
            <button
                v-for="action in visibleActionsFor(athlete)"
                :key="action"
                class="btn btn-sm btn_actions color-gray"
                :disabled="action === 'start'
                  ? ((!cameraReady && allowRecording) || hasRunningTimer || recordingLocked || athlete.status_id === 17 || isProgressActive(getUploadProgress(athlete)))
                  : (action === 'stop' ? hasRunningTimer : false)"
                @click.stop="handleEmit(action, athlete, stageName, roundNumber)"
                :title="action === 'start' && !cameraReady ? $t('judge.camera.start_required') : $t('judge.' + action)"
            >
              <span
                  v-if="action === 'start'"
                  class="tooltip-wrapper"
                  :title="!cameraReady ? $t('judge.camera.start_required') : $t('judge.' + action)"
              >
                <template v-if="getUploadProgress(athlete)">
                  <span
                    class="progress-circle"
                    :class="{ 'progress-circle--done': isProgressComplete(getUploadProgress(athlete)) }"
                  >
                    {{ getUploadProgress(athlete).percent }}%
                  </span>
                </template>
                <i v-else :class="getIconClass(action)"></i>
              </span>
              <i v-else :class="getIconClass(action)"></i>
            </button>
          </div>
          <div
            v-if="shouldShowUploadFeedback(getUploadProgress(athlete))"
            :class="uploadFeedbackClass(getUploadProgress(athlete))"
          >
            {{ uploadFeedbackText(getUploadProgress(athlete)) }}
          </div>
        </div>
      </div>

      <button class="nav-arrow" @click="nextPage" :disabled="!hasNextPage">
        <i class="fa-solid fa-chevron-right"></i>
      </button>
    </div>
    <div v-else class="no-athletes">{{ $t('judge.no_athletes') }}</div>
  </div>

  <div v-else-if="athletes.length && !previewModalOpen" class="judge-table-container">
  <table class="judge-table">
    <thead>
    <tr class="text-center">
      <th>#</th>
      <th><span class="col-label">{{ $t("judge.athlete_name") }}</span></th>
      <th>{{ $t("judge.actions") }}</th>
      <th v-if="!allowRecording">{{ $t("judge.status") }}</th>
      <th v-if="!allowRecording">{{ $t("judge.review") }}</th>
      <th v-if="!allowRecording">
        <div class="score-header">
          <span class="score-label">{{ $t("judge.score") }}</span>
          <button @click="showScore = !showScore" class="btn btn-sm">
            <i :class="showScore ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
          </button>
        </div>
      </th>
      <th v-if="!allowRecording" class="aux-head"></th>


    </tr>
    </thead>
    <tbody v-if="!isVsMode">
    <tr
      v-for="(athlete, index) in paginatedFilteredAthletes"
      :key="athlete.id"
      :class="{ 'row-clickable': canRespondToRowClick(athlete) }"
      @click="handleRowClick(athlete, stageName, roundNumber)"
    >
      <td class="athlete-header-cell">
        <div class="athlete-header">
          <span class="number">{{ tablePageStart + index + 1 }}</span>
          <img :src="athlete.user.img_path || defaultAvatar" class="athlete-img" alt="" />
        </div>
      </td>
      <td :data-label="$t('judge.athlete_name')">
        <div class="athlete-name">
          <span class="athlete-name-text">{{ athlete.user.name }} {{ athlete.user.surname }}</span>
        </div>
      </td>
      <td :data-label="$t('judge.actions')" class="actions">
        <div class="action-buttons">
          <div class="primary-actions">
            <button
              v-if="getVisibleButtons(athlete).includes('start')"
              class="btn btn-sm btn_actions color-gray"
              :disabled="
                (!cameraReady && allowRecording)
                || hasRunningTimer
                || recordingLocked
                || athlete.status_id === 17
                || isProgressActive(getUploadProgress(athlete))
              "
              @click.stop="$emit('start-recording', athlete)"
            >
              <span
                class="tooltip-wrapper"
                :title="!cameraReady ? $t('judge.camera.start_required') : $t('judge.start_recording')"
              >
                <template v-if="getUploadProgress(athlete)">
                  <span
                    class="progress-circle"
                    :class="{ 'progress-circle--done': getUploadProgress(athlete).status === 'done' }"
                  >
                    {{ getUploadProgress(athlete).percent }}%
                  </span>
                </template>
                <i v-else class="fa-solid fa-eye text-primary"></i>
              </span>
            </button>

            <button
              v-if="getVisibleButtons(athlete).includes('stop')"
              @click.stop="$emit('stop-recording', athlete)"
              class="btn btn-sm btn_actions color-gray"
              :disabled="hasRunningTimer"
              :title="$t('judge.stop_recording')"
            >
              <i class="fa-solid fa-stop text-warning"></i>
            </button>

            <button
              v-if="getVisibleButtons(athlete).includes('score')"
              :disabled="isDisabledButton('score', athlete)"
              @click.stop="handleEmit('score', athlete, stageName, roundNumber)"
              :class="scoreActionButtonClass(athlete)"
              :title="$t('judge.score')"
            >
              <i class="fas fa-gavel"></i>
            </button>

            <button
              v-if="getVisibleButtons(athlete).includes('edit')"
              :disabled="isDisabledButton('edit', athlete)"
              @click.stop="handleEmit('edit', athlete, stageName, roundNumber)"
              class="btn btn-sm btn_actions primary-btn alt"
              :title="$t('judge.edit_score')"
            >
              <i class="fas fa-pen-to-square"></i>
            </button>
          </div>
        </div>
      </td>
      <td v-if="!allowRecording" :data-label="$t('judge.status')" class="status text-center">
          <span :class="statusClass(athlete.status_id)">
            {{ getStatusLabel(athlete.status_id) }}
          </span>
      </td>
      <td v-if="!allowRecording" :data-label="$t('judge.review')" class="review text-center align-middle">
        <!-- Línea superior: doble check + badge -->
        <div v-if="isOwner(athlete)" class="d-flex justify-content-center align-items-center gap-2 mb-1">
          <!-- Check doble -->
          <span class="check-wrapper">
      <i
          class="fas fa-check"
          :style="{ color: isValid(athlete) ? '#007bff' : '#6c757d' }"
      ></i>
      <i
          class="fas fa-check"
          :style="{
          color: isValid(athlete) ? '#007bff' : '#6c757d',
          marginLeft: '-6px',
          opacity: isValid(athlete) ? 1 : 0.3
        }"
      ></i>
    </span>

          <!-- Badge con número -->
          <span
              class="error-badge"
              :style="{
        backgroundColor: !isValid(athlete)
          ? '#6c757d'
          : getErrorCount(athlete) > 0
            ? '#dc3545'
            : '#28a745'
      }"
          >
      {{ getErrorCount(athlete) }}
    </span>
        </div>

        <!-- Línea inferior: estado -->
        <div class="status-message">
            <span v-if="isInjured(athlete)" class="message-injured">
              <span class="icon-wrapper">
                <i class="fas fa-user-injured" title="Lastimado" style="color: #dc3545;"></i>
              </span>
            </span>
          <span v-else-if="isEliminated(athlete)" class="message-eliminated">
              <span class="icon-wrapper">
                <i class="fas fa-user-slash" style="color: #6c757d;"></i>
              </span>
           </span>
        </div>
      </td>
      <!-- 🎯 Columna puntaje -->
        <td
          v-if="!allowRecording"
          :data-label="$t('judge.score')"
          class="score text-center score-cell"
      >
        <!-- Botón visible solo en móvil -->
        <button
            class="toggle-score-mobile"
            @click.stop="showScore = !showScore"
          :aria-label="showScore ? $t('common.hide') : $t('common.show')"
        >
          <i :class="showScore ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
        </button>
        <template v-if="showScore && !isSelfAthlete(athlete)">
          <span class="score-span">{{ formatScore(athlete.total_score) }}</span>
        </template>
        <template v-else>
          —
        </template>

      </td>
      <td v-if="!allowRecording" class="minor-actions" :data-label="$t('judge.actions')">
        <div class="minor-actions-col">
          <button
            v-if="getVisibleButtons(athlete).includes('disqualify')"
            :disabled="isDisabledButton('disqualify', athlete)"
            @click.stop="$emit('disqualify', athlete)"
            class="btn btn-sm btn_actions tiny-btn"
            :title="$t('judge.disqualify')"
          >
            <i class="fas fa-ban"></i>
          </button>
          <button
            v-if="getVisibleButtons(athlete).includes('injury')"
            :disabled="isDisabledButton('injury', athlete)"
            @click.stop="$emit('injury', athlete)"
            class="btn btn-sm btn_actions tiny-btn"
            :title="$t('judge.injury')"
          >
            <i class="fas fa-user-injured"></i>
          </button>
        </div>
      </td>
    </tr>
    </tbody>

    <tbody v-else>
    <template v-for="(pair, index) in paginatedGroupedAthletesVs" :key="pairTableKey(pair, index)">
      <tr
        v-for="(athlete, aIndex) in pair"
        :key="athlete.id"
        :class="{ 'row-clickable': canRespondToRowClick(athlete) }"
        @click="handleRowClick(athlete, stageName, roundNumber)"
      >
        <td class="athlete-header-cell">
          <div class="athlete-header">
            <span class="number">{{ (tablePairPageStart + index) * 2 + aIndex + 1 }}</span>
            <img :src="athlete.user.img_path || defaultAvatar" class="athlete-img" alt="" />
          </div>
        </td>
        <td :data-label="$t('judge.athlete_name')">
          <div class="athlete-name">
            <span class="athlete-name-text">{{ athlete.user.name }} {{ athlete.user.surname }}</span>
          </div>
        </td>
        <!-- 🎯 CELDA CON ROWSPAN: primera fila, ambos activos -->
        <td
            v-if="aIndex === 0 && !areBothStatus(pair) && !pair.some(a => isDisqualifiedOrInjured(a))"
            :rowspan="2"
            :data-label="$t('judge.actions')"
            class="actions align-middle text-center"
        >
          <button
              v-if="getVisibleButtons(pair[0]).includes('start')"
              class="btn btn-sm btn_actions color-gray"
              :disabled="
                (!cameraReady && allowRecording)
                || hasRunningTimer
                || recordingLocked
                || isProgressActive(getUploadProgress(pair[0]))
                || isProgressActive(getUploadProgress(pair[1]))
              "
              @click.stop="emitDual('start-recording', pair)"
          >
            <span
                class="tooltip-wrapper"
                :title="!cameraReady ? $t('judge.camera.start_required') : $t('judge.start_recording')"
            >
              <template v-if="getUploadProgress(pair[0]) || getUploadProgress(pair[1])">
                <span
                  class="progress-circle"
                  :class="{ 'progress-circle--done': (isProgressComplete(getUploadProgress(pair[0])) && isProgressComplete(getUploadProgress(pair[1]))) }"
                >
                  {{ getUploadProgress(pair[0])?.percent || getUploadProgress(pair[1])?.percent || 0 }}%
                </span>
              </template>
              <i v-else class="fa-solid fa-eye text-primary"></i>
            </span>
          </button>
          <button
              v-if="getVisibleButtons(pair[0]).includes('stop')"
              @click.stop="emitDual('stop-recording', pair)"
              class="btn btn-sm btn_actions color-gray"
              :disabled="hasRunningTimer"
          >
            <i class="fa-solid fa-stop text-warning"></i>
          </button>
        </td>

        <!-- ✅ CELDA CON ACCIONES INDIVIDUALES POR FILA -->
        <td
            v-else-if="areBothStatus(pair) || pair.some(a => isDisqualifiedOrInjured(a))"
            :data-label="$t('judge.actions')"
            class="actions text-center"
        >
          <div class="action-buttons">
            <template v-for="(row, rowIndex) in chunkButtons(visibleVsRowActions(athlete), 2)" :key="rowIndex">
              <div class="button-row">
                <template v-for="action in row" :key="action">
                  <button
                      :class="actionButtonClass(action, athlete)"
                      :disabled="
                        isDisabledButton(action, athlete)
                        || (action === 'start' && ((!cameraReady && allowRecording) || hasRunningTimer || recordingLocked || athlete.status_id === 17 || isProgressActive(getUploadProgress(athlete))))
                        || (action === 'stop' && hasRunningTimer)
                      "
                      @click.stop="handleEmit(action, athlete, stageName, roundNumber)"
                      :title="action === 'start' && !cameraReady ? $t('judge.camera.start_required') : $t('judge.' + action)"
                  >
                    <span
                        v-if="action === 'start'"
                        class="tooltip-wrapper"
                        :title="!cameraReady ? $t('judge.camera.start_required') : $t('judge.' + action)"
                    >
                      <template v-if="getUploadProgress(athlete)">
                        <span
                          class="progress-circle"
                          :class="{ 'progress-circle--done': isProgressComplete(getUploadProgress(athlete)) }"
                        >
                          {{ getUploadProgress(athlete).percent }}%
                        </span>
                      </template>
                      <i v-else :class="getIconClass(action)"></i>
                    </span>
                    <i v-else :class="getIconClass(action)"></i>
                  </button>
                </template>
              </div>
            </template>
          </div>
        </td>

        <!-- 🎯 Botones individuales -->
        <td v-if="!allowRecording" :data-label="$t('judge.status')" class="status text-center">
        <span :class="statusClass(athlete.status_id)">
          {{ getStatusLabel(athlete.status_id) }}
        </span>
        </td>
        <td v-if="!allowRecording" :data-label="$t('judge.review')" class="review text-center align-middle">
          <!-- Línea superior: doble check + badge -->
          <div v-if="isOwner(athlete)" class="d-flex justify-content-center align-items-center gap-2 mb-1">
            <!-- Check doble -->
            <span class="check-wrapper">
      <i
          class="fas fa-check"
          :style="{ color: isValid(athlete) ? '#007bff' : '#6c757d' }"
      ></i>
      <i
          class="fas fa-check"
          :style="{
          color: isValid(athlete) ? '#007bff' : '#6c757d',
          marginLeft: '-6px',
          opacity: isValid(athlete) ? 1 : 0.3
        }"
      ></i>
    </span>

            <!-- Badge con número -->
            <span
                class="error-badge"
                :style="{
        backgroundColor: !isValid(athlete)
          ? '#6c757d'
          : getErrorCount(athlete) > 0
            ? '#dc3545'
            : '#28a745'
      }"
            >
      {{ getErrorCount(athlete) }}
    </span>
          </div>

          <!-- Línea inferior: estado -->
          <div class="status-message">
            <span v-if="isInjured(athlete)" class="message-injured">
              <span class="icon-wrapper">
                <i class="fas fa-user-injured" title="Lastimado" style="color: #dc3545;"></i>
              </span>
            </span>
            <span v-else-if="isEliminated(athlete)" class="message-eliminated">
              <span class="icon-wrapper">
                <i class="fas fa-user-slash" style="color: #6c757d;"></i>
              </span>
           </span>
          </div>
        </td>

        <!-- 🎯 Columna puntaje -->
        <td
          v-if="!allowRecording"
          :data-label="$t('judge.score')"
          class="score text-center score-cell"
        >
          <!-- Botón visible solo en móvil -->
          <button
              class="toggle-score-mobile"
              @click.stop="showScore = !showScore"
              :aria-label="showScore ? $t('common.hide') : $t('common.show')"
          >
            <i :class="showScore ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
          </button>
          <template v-if="showScore && !isSelfAthlete(athlete)">
            <span class="score-span">{{ formatScore(athlete.total_score) }}</span>
          </template>
          <template v-else>
            —
          </template>

        </td>

        <td v-if="!allowRecording" class="minor-actions" :data-label="$t('judge.actions')">
          <div class="minor-actions-col">
            <button
              v-if="getVisibleButtons(athlete).includes('disqualify')"
              :disabled="isDisabledButton('disqualify', athlete)"
              @click.stop="$emit('disqualify', athlete)"
              class="btn btn-sm btn_actions tiny-btn"
              :title="$t('judge.disqualify')"
            >
              <i class="fas fa-ban"></i>
            </button>
            <button
              v-if="getVisibleButtons(athlete).includes('injury')"
              :disabled="isDisabledButton('injury', athlete)"
              @click.stop="$emit('injury', athlete)"
              class="btn btn-sm btn_actions tiny-btn"
              :title="$t('judge.injury')"
            >
              <i class="fas fa-user-injured"></i>
            </button>
          </div>
        </td>


      </tr>
    </template>
    </tbody>

  </table>

  <div
    v-if="showTablePagination"
    class="table-pagination"
    role="navigation"
    :aria-label="$t('competitions.pagination.page') || 'Pagination'"
  >
    <button
      type="button"
      class="btn btn-sm btn-outline-secondary"
      :disabled="isVsMode ? tablePairPage <= 1 : tablePage <= 1"
      @click="goToPreviousTablePage"
    >
      {{ $t('competitions.pagination.prev') || 'Previous' }}
    </button>

    <span class="table-pagination-status">
      {{ $t('competitions.pagination.page') || 'Page' }}
      {{ isVsMode ? tablePairPage : tablePage }}
      {{ $t('competitions.pagination.of') || 'of' }}
      {{ isVsMode ? tablePairTotalPages : tableTotalPages }}
    </span>

    <button
      type="button"
      class="btn btn-sm btn-outline-secondary"
      :disabled="isVsMode ? tablePairPage >= tablePairTotalPages : tablePage >= tableTotalPages"
      @click="goToNextTablePage"
    >
      {{ $t('competitions.pagination.next') || 'Next' }}
    </button>
  </div>
  </div>
<!--  <pre>{{ JSON.stringify(filteredAthletes, null, 2) }}</pre>-->
</template>

<script>
import {isOwnerJudgeFromCompetition, isOwnerOrganizer, isSupervisor} from "@/helpers/roleUtils";
import { assembleVsBracketMatches } from "@/helpers/vsBracketAssembly";
import {mapState} from "vuex";

export default {
  name: "JudgeRoundTable",
  props: {
    athletes: {
      type: Array,
      required: true
    },
    isUploadingRecorded: {
      type: Boolean,
      default: false,
    },
    stageName: {
      type: String,
      required: false,
      default: null,
      validator: (value) => value === null || typeof value === 'string',
    },
    roundNumber: {
      type: Number,
      required: true
    },
     uploadProgress: {
       type: Object,
       default: () => ({}),
     },
    allowRecording: {
      type: Boolean,
      default: true,
    },
    cameraReady: {
      type: Boolean,
      default: true,
    },
    recordingLocked: {
      type: Boolean,
      default: false,
    },
    recordedAthleteId: {
      type: [Number, String],
      default: null,
    },
    recordedPairIds: {
      type: Array,
      default: () => [],
    },
    previewModalOpen: {
      type: Boolean,
      default: false,
    },
    hasRunningTimer: {
      type: Boolean,
      default: false,
    },
    initialTablePage: {
      type: Number,
      default: 1,
    },
    initialTablePairPage: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      defaultAvatar: require("@/assets/user.png"),
      showScore: false,
      currentIndex: 0,
      currentPairIndex: 0,
      tablePage: this.normalizePageValue(this.initialTablePage),
      tablePairPage: this.normalizePageValue(this.initialTablePairPage),
      baseItemsPerPage: 5,
      screenWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
      initialOrderMap: {},
    };
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapState("competition",["competitionIdToView", "competitionData"]),
    isQualificationStage() {
      if ([1, 2].includes(Number(this.roundNumber))) {
        return true;
      }

      const athletes = Array.isArray(this.athletes) ? this.athletes : [];
      const hasRoundSizeId = athletes.some((athlete) => {
        const roundSizeId = athlete?.round_size_id;
        return roundSizeId !== null && roundSizeId !== undefined && roundSizeId !== '';
      });

      if (!hasRoundSizeId) {
        return true;
      }

      const raw = this.stageName ?? this.competitionData?.stage_name ?? '';
      const name = typeof raw === 'string' ? raw : String(raw || '');
      return !name.trim();
    },
    filteredAthletes() {
      // Include new backend statuses (20, 21) so they show up in both video slider and judge table
      const allowedStatuses = [6, 7, 10, 11, 12, 13, 14, 16, 17, 19, 20, 21, 22, 23];
      return this.athletes.filter(a => allowedStatuses.includes(a.status_id));
    },
    hasVsMatchMetadata() {
      return this.filteredAthletes.some((athlete) => {
        const bracket = athlete?.vs_bracket || null;
        const group = String(bracket?.group || athlete?.vs_group || '').trim();
        const matchKey = String(bracket?.match_key || '').trim();
        const matchNumberRaw = bracket?.match_number ?? athlete?.vs_match_number;
        const matchNumber = Number(matchNumberRaw);

        if (matchKey) {
          return true;
        }

        return group !== '' && Number.isFinite(matchNumber) && matchNumber > 0;
      });
    },
    canonicalVsMatches() {
      const hasVsFlag = Boolean(this.competitionData?.is_vs || this.competitionData?.competition?.is_vs);
      if (!hasVsFlag || this.isQualificationStage || !this.hasVsMatchMetadata) return [];

      const normalizedAthletes = this.filteredAthletes.map((athlete) => ({
        ...athlete,
        originalRound: athlete,
        id: athlete?.user?.id ?? athlete?.user_id ?? athlete?.id ?? null,
        round_id: athlete?.id ?? athlete?.round_id ?? null,
        name: athlete?.user?.name ?? athlete?.name ?? '',
        surname: athlete?.user?.surname ?? athlete?.surname ?? '',
        img: athlete?.user?.img_path ?? athlete?.img ?? athlete?.img_path ?? null,
        total_score: athlete?.total_score,
        is_vs_winner: athlete?.is_vs_winner,
        vs_group: athlete?.vs_group,
        vs_match_number: athlete?.vs_match_number,
        vs_bracket: athlete?.vs_bracket || null,
      }));

      return assembleVsBracketMatches(normalizedAthletes, {
        stageId: this.stageName || this.roundNumber || 'judge-stage',
        placeholderName: this.$t('rounds.placeholder_player') || 'TBD',
      });
    },
    isVsMode() {
      return this.canonicalVsMatches.length > 0;
    },
    groupedAthletesVs() {
      if (!this.isVsMode) return [];

      return this.canonicalVsMatches
        .map((match) => match.participants
          .map((participant) => participant?.sourceRound?.originalRound || participant?.sourceRound || null)
          .filter((athlete) => athlete && athlete.id))
        .filter((pair) => pair.length > 0);
    },
    blockedVsAthleteIds() {
      const blocked = new Set();
      if (!this.isVsMode || !this.filteredAthletes?.length || !this.user?.id) return blocked;

      this.groupedAthletesVs.forEach((pair) => {
        if (pair.some(a => a.user?.id === this.user.id)) {
          pair.forEach(a => blocked.add(a.id));
        }
      });

      return blocked;
    },
    sliderAthletes() {
      if (!this.allowRecording) return [];

      // Preserve initial order for stable sorting; do not push recorded to the end
      this.filteredAthletes.forEach((athlete, idx) => {
        if (this.initialOrderMap[athlete.id] === undefined) {
          this.initialOrderMap = {
            ...this.initialOrderMap,
            [athlete.id]: idx,
          };
        }
      });

      // Only push to end when upload finished (status 21 or upload marked done); otherwise keep initial order
      const shouldGoLast = (athlete) => {
        const upload = this.uploadProgress?.[athlete.id];
        const uploadDone = upload?.status === 'done';
        return athlete?.status_id === 21 || uploadDone;
      };

      return [...this.filteredAthletes].sort((a, b) => {
        const lastA = shouldGoLast(a) ? 1 : 0;
        const lastB = shouldGoLast(b) ? 1 : 0;
        if (lastA !== lastB) return lastA - lastB;
        const orderA = this.initialOrderMap[a.id] ?? 0;
        const orderB = this.initialOrderMap[b.id] ?? 0;
        return orderA - orderB;
      });
    },
    pageSize() {
      return this.screenWidth <= 768 ? 1 : this.baseItemsPerPage;
    },
    currentAthlete() {
      if (!this.sliderAthletes.length) return null;
      const idx = Math.min(this.currentIndex, this.sliderAthletes.length - 1);
      return this.sliderAthletes[idx] || null;
    },
    currentButtons() {
      return this.currentAthlete ? this.getVisibleButtons(this.currentAthlete) : [];
    },
    pageStart() {
      return Math.floor(this.currentIndex / this.pageSize) * this.pageSize;
    },
    visibleAthletes() {
      return this.sliderAthletes.slice(this.pageStart, this.pageStart + this.pageSize);
    },
    hasPrevPage() {
      return this.pageStart > 0;
    },
    hasNextPage() {
      return this.pageStart + this.pageSize < this.sliderAthletes.length;
    },
    vsPairs() {
      if (!this.isVsMode) return [];

      const shouldPairGoLast = (pair) => this.isPairUploadDone(pair);
      const pairOrder = (pair) => {
        const orderCandidates = [pair.left?.id, pair.right?.id]
          .filter(Boolean)
          .map((id) => this.initialOrderMap[id])
          .filter((value) => value !== undefined);

        return orderCandidates.length ? Math.min(...orderCandidates) : Number.MAX_SAFE_INTEGER;
      };

      return this.canonicalVsMatches
        .map((match) => {
          const participants = match.participants
            .map((participant) => participant?.sourceRound?.originalRound || participant?.sourceRound || null)
            .filter((athlete) => athlete && athlete.id);
          const left = participants[0] || null;
          const right = participants[1] || null;

          return {
            key: match.key,
            left,
            right,
            leftName: [left?.user?.name, left?.user?.surname].filter(Boolean).join(' '),
            rightName: [right?.user?.name, right?.user?.surname].filter(Boolean).join(' '),
            matchNumber: match.matchNumber || left?.vs_match_number || right?.vs_match_number || '',
            group: match.group || left?.vs_group || right?.vs_group || '',
            displayOrder: match.displayOrder ?? null,
          };
        })
        .sort((leftPair, rightPair) => {
          const lastLeft = shouldPairGoLast(leftPair) ? 1 : 0;
          const lastRight = shouldPairGoLast(rightPair) ? 1 : 0;

          if (lastLeft !== lastRight) {
            return lastLeft - lastRight;
          }

          const leftDisplayOrder = Number(leftPair.displayOrder);
          const rightDisplayOrder = Number(rightPair.displayOrder);
          if (Number.isFinite(leftDisplayOrder) && Number.isFinite(rightDisplayOrder) && leftDisplayOrder !== rightDisplayOrder) {
            return leftDisplayOrder - rightDisplayOrder;
          }

          return pairOrder(leftPair) - pairOrder(rightPair);
        });
    },
    vsPageSize() {
      return this.screenWidth <= 768 ? 1 : Math.max(1, Math.floor(this.baseItemsPerPage / 2));
    },
    vsPageStart() {
      return Math.floor(this.currentPairIndex / this.vsPageSize) * this.vsPageSize;
    },
    visibleVsPairs() {
      return this.vsPairs.slice(this.vsPageStart, this.vsPageStart + this.vsPageSize);
    },
    hasPrevVsPage() {
      return this.vsPageStart > 0;
    },
    hasNextVsPage() {
      return this.vsPageStart + this.vsPageSize < this.vsPairs.length;
    },
    uploadActionsLocked() {
      const entries = Object.values(this.uploadProgress || {});
      if (!entries.length) return false;
      return entries.some((progress) => this.isProgressActive(progress));
    },
    tablePageSize() {
      return this.screenWidth <= 576 ? 6 : 10;
    },
    tablePageStart() {
      return (this.tablePage - 1) * this.tablePageSize;
    },
    tableTotalPages() {
      return Math.max(1, Math.ceil(this.filteredAthletes.length / this.tablePageSize));
    },
    paginatedFilteredAthletes() {
      return this.filteredAthletes.slice(this.tablePageStart, this.tablePageStart + this.tablePageSize);
    },
    tablePairPageSize() {
      return this.screenWidth <= 576 ? 2 : 4;
    },
    tablePairPageStart() {
      return (this.tablePairPage - 1) * this.tablePairPageSize;
    },
    tablePairTotalPages() {
      return Math.max(1, Math.ceil(this.groupedAthletesVs.length / this.tablePairPageSize));
    },
    paginatedGroupedAthletesVs() {
      return this.groupedAthletesVs.slice(this.tablePairPageStart, this.tablePairPageStart + this.tablePairPageSize);
    },
    showTablePagination() {
      return this.isVsMode ? this.tablePairTotalPages > 1 : this.tableTotalPages > 1;
    }
  },
  watch: {
    sliderAthletes(newVal) {
      if (!newVal.length) {
        this.currentIndex = 0;
        return;
      }
      if (this.currentIndex > newVal.length - 1) {
        this.currentIndex = newVal.length - 1;
      }
    },
    vsPairs(newVal) {
      if (!newVal.length) {
        this.currentPairIndex = 0;
        return;
      }
      if (this.currentPairIndex > newVal.length - 1) {
        this.currentPairIndex = newVal.length - 1;
      }
    },
    filteredAthletes() {
      this.ensureValidTablePage();
    },
    groupedAthletesVs() {
      this.ensureValidTablePairPage();
    },
    isVsMode() {
      this.ensureValidTablePage();
      this.ensureValidTablePairPage();
    },
    initialTablePage(newVal) {
      this.tablePage = this.normalizePageValue(newVal);
      this.ensureValidTablePage();
    },
    initialTablePairPage(newVal) {
      this.tablePairPage = this.normalizePageValue(newVal);
      this.ensureValidTablePairPage();
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize, { passive: true });
  },
  methods: {
    isOwnerOrganizer,
    isOwnerJudgeFromCompetition,
    normalizePageValue(value) {
      const normalized = Number(value);
      return Number.isInteger(normalized) && normalized > 0 ? normalized : 1;
    },
    getTableNavigationState() {
      return {
        tablePage: this.tablePage,
        tablePairPage: this.tablePairPage,
      };
    },
    getUploadProgress(athlete) {
      const id = athlete?.id;
      return id ? this.uploadProgress[id] : null;
    },
    progressStatus(progress) {
      return String(progress?.status || '').toLowerCase();
    },
    isProgressActive(progress) {
      const status = this.progressStatus(progress);
      const percent = Number(progress?.percent);

      if (status === 'uploading') {
        return !Number.isFinite(percent) || percent < 100;
      }

      return false;
    },
    isProgressComplete(progress) {
      if (!progress) return false;

      const status = this.progressStatus(progress);
      return status === 'done';
    },
    shouldRenderProgress(progress, target = null) {
      return this.isProgressActive(progress)
        || this.isProgressComplete(progress)
        || this.progressStatus(progress) === 'processing_youtube'
        || this.canMarkProcessing(progress, target);
    },
    shouldShowUploadFeedback(progress) {
      const status = this.progressStatus(progress);
      return Boolean(progress?.message) && status === 'error';
    },
    uploadFeedbackText(progress) {
      return progress?.message || '';
    },
    uploadFeedbackClass() {
      return 'upload-feedback upload-feedback--error';
    },
    uploadHasError(athlete) {
      const progress = this.getUploadProgress(athlete);
      return progress?.status === 'error';
    },
    canResumeProgress(progress) {
      if (!progress || this.uploadActionsLocked) return false;
      return progress.status === 'uploading' || progress.status === 'error';
    },
    resolveUploadTargets(target = null) {
      if (!target) return [];
      if (Array.isArray(target)) return target.filter(Boolean);

      if (target.left || target.right) {
        return [target.left, target.right].filter(Boolean);
      }

      return [target].filter(Boolean);
    },

    hasConfirmedUploadTarget(target = null) {
      return this.resolveUploadTargets(target).some((entry) => this.isUploadDone(entry));
    },

    canMarkProcessing(progress, target = null) {
      if (this.hasConfirmedUploadTarget(target)) {
        return false;
      }

      if (this.hasPersistedPlaylistVideo(target) && this.progressStatus(progress) === 'error') {
        return true;
      }

      if (!progress) return false;

      const status = this.progressStatus(progress);
      const percent = Number(progress?.percent);

      if (status === 'processing_youtube') return true;
      if (status === 'uploading' && Number.isFinite(percent) && percent >= 100) return true;

      return false;
    },
    canResumeAthlete(athlete) {
      if (!athlete) return false;
      return this.canResumeProgress(this.getUploadProgress(athlete)) && !this.recordingLocked;
    },
    canResumePair(pair) {
      if (!pair) return false;
      return this.canResumeProgress(this.getPairUploadProgress(pair)) && !this.recordingLocked;
    },
    isUploadDone(athlete) {
      const progress = this.getUploadProgress(athlete) || {};
      const statusId = athlete?.status_id;
      if (progress.status === 'done') return true;
      return statusId === 21 || statusId === 10;
    },
    markProcessing(athlete) {
      if (!athlete?.id) return;
      this.$emit('update-status', { id: athlete.id, statusId: 21 });
    },
    markPairProcessing(pair) {
      if (!pair) return;
      const ids = [pair.left?.id, pair.right?.id].filter(Boolean);
      ids.forEach((id) => this.$emit('update-status', { id, statusId: 21 }));
    },
    shouldShowUpload(athlete) {
      const progress = this.getUploadProgress(athlete);
      if (progress) return true;
      const id = athlete?.id;
      if (!id) return false;
      if (this.recordedAthleteId && String(id) === String(this.recordedAthleteId)) return true;
      return this.hasRecoverableRecordedStatus(athlete);
    },
    shouldShowPairUpload(pair) {
      if (!pair) return false;
      const progress = this.getPairUploadProgress(pair);
      if (progress) return true;
      const ids = [pair.left?.id, pair.right?.id].filter(Boolean).map(String);
      const recorded = (this.recordedPairIds || []).map(String);
      const hasUploadableStatus = this.hasRecoverableRecordedPairStatus(pair);
      const matchesRecordedPair = ids.length && ids.every((id) => recorded.includes(id));
      return matchesRecordedPair || hasUploadableStatus;
    },
    shouldShowPreview(athlete) {
      if (!athlete) return false;
      const progress = this.getUploadProgress(athlete);
      if (this.canMarkProcessing(progress, athlete) || this.isUploadDone(athlete)) return false;
      return this.hasPendingRecordedAthlete(athlete) || this.hasRecoverableRecordedStatus(athlete);
    },
    shouldShowPairPreview(pair) {
      if (!pair) return false;
      const progress = this.getPairUploadProgress(pair);
      if (this.canMarkProcessing(progress, pair) || this.isPairUploadDone(pair)) return false;
      return this.hasPendingRecordedPair(pair) || this.hasRecoverableRecordedPairStatus(pair);
    },
    hasPendingRecordedAthlete(athlete) {
      const athleteId = athlete?.id;
      if (!athleteId || !this.recordedAthleteId) return false;
      return String(athleteId) === String(this.recordedAthleteId);
    },
    hasPendingRecordedPair(pair) {
      if (!pair) return false;
      const pairIds = [pair.left?.id, pair.right?.id].filter(Boolean).map(String);
      const recordedIds = (this.recordedPairIds || []).map(String);
      return Boolean(pairIds.length) && pairIds.every((id) => recordedIds.includes(id));
    },
    shouldShowDelete(athlete) {
      if (!athlete) return false;
      if (this.isUploadDone(athlete) || this.canMarkProcessing(this.getUploadProgress(athlete), athlete)) return false;
      return this.hasPendingRecordedAthlete(athlete) || this.hasRecoverableRecordedStatus(athlete);
    },
    shouldShowPairDelete(pair) {
      if (!pair) return false;
      if (this.isPairUploadDone(pair) || this.canMarkProcessing(this.getPairUploadProgress(pair), pair)) return false;
      return this.hasPendingRecordedPair(pair) || this.hasRecoverableRecordedPairStatus(pair);
    },
    hasPersistedPlaylistVideo(target) {
      const targets = this.resolveUploadTargets(target);
      return targets.some((entry) => {
        const playlistVideoId = entry?.playlist_video_id || entry?.playlistVideo?.id || entry?.playlistVideo?.video_id;
        const playlistVideoStatus = entry?.playlistVideo?.status || entry?.playlist_video_status || '';
        const youtubeStatus = entry?.playlistVideo?.youtube_status || entry?.youtube_status || '';
        return Boolean(playlistVideoId || playlistVideoStatus || youtubeStatus);
      });
    },
    hasRecoverableRecordedStatus(athlete) {
      const statusId = Number(athlete?.status_id);
      if (statusId === 17) return true;
      if (statusId === 20) return !this.hasPersistedPlaylistVideo(athlete);
      return false;
    },
    hasRecoverableRecordedPairStatus(pair) {
      return [pair?.left, pair?.right]
        .filter(Boolean)
        .some((athlete) => this.hasRecoverableRecordedStatus(athlete));
    },
    isPairUploadDone(pair) {
      if (!pair) return false;
      const leftDone = this.isUploadDone(pair.left || {});
      const rightDone = this.isUploadDone(pair.right || {});
      const progress = this.getPairUploadProgress(pair);
      const progressDone = progress && progress.status === 'done';
      return leftDone || rightDone || Boolean(progressDone);
    },
    visibleActionsFor(athlete) {
      const status = athlete?.status_id;
      if (status === 6 || status === 7) return [];
      return this.getVisibleButtons(athlete).filter(action => action !== 'view' && !(action === 'start' && this.shouldShowUpload(athlete)));
    },
    getStatusLabel(status_id) {
      const labels = {
        13: this.$t("judge.pending"),
        6: this.$t("judge.scoring"),
        11: this.$t("judge.disqualified"),
        12: this.$t("judge.injured"),
        14: this.$t("judge.eliminated"),
        7: this.$t("judge.completed"),
        16: this.$t("judge.recording"),
        17: this.$t("judge.recorded"),
        10: this.$t("judge.published"),
        19: this.$t("judge.review"),
        20: this.$t('judge.status_20') || 'Estado 20',
        21: this.$t('judge.status_21') || 'Estado 21',
        22: this.$t('judge.status_22') || 'Editing',
        23: this.$t('judge.status_23') || this.$t('judge.no_submission') || 'No submission',
      };
      return labels[status_id] || String(status_id || '');
    },
    statusClass(status_id) {
      return {
        "status-pending": status_id === 13,
        "status-scoring": status_id === 6,
        "status-disqualified": status_id === 11,
        "status-injured": status_id === 12,
        "status-completed": status_id === 7,
        "status-recording": status_id === 16,
        "status-recorded": status_id === 17,
        "status-published": status_id === 10,
        "status-review": status_id === 19 || status_id === 22 || status_id === 23,
        "status-queue": status_id === 20,
        "status-processing": status_id === 21,
      };
    },
    getVisibleButtons(athlete) {
      const status = athlete.status_id;
      if (status === 13) {
        return this.allowRecording ? ['start'] : [];
      }

      if (status === 16) {
        return this.allowRecording ? ['start'] : [];
      }

      if (status === 17) {
        return this.allowRecording ? ['start'] : [];
      }

      if (status === 21) {
        return this.allowRecording ? ['start'] : ['score', 'disqualify', 'injury'];
      }

      if (status === 6) {
        return ['edit', 'disqualify', 'injury','view'];
      }

      if (status === 7) {
        return ['edit', 'disqualify', 'injury','view'];
      }
      if (status === 23) {
        return ['view'];
      }
      if (status === 19 || status === 22) {
        return ['edit', 'disqualify', 'injury','view'];
      }
      if (status === 12) {
        return ['edit', 'disqualify', 'injury','view'];
      }
      if (status === 11) {
        return ['edit', 'disqualify', 'injury','view'];
      }

      return [];
    },
    isDisabledButton(type, athlete) {
      const status = athlete?.status_id;
      const currentUserId = this.user?.id || null;
      const athleteUserId = athlete?.user?.id || null;

      // Sin sesión → todo deshabilitado
      if (!currentUserId) return true;

      // En VS (solo etapas posteriores a clasificación), si el juez participa en ese match (él o su oponente), bloquea todo
      if (this.competitionData?.is_vs && !this.isQualificationStage && this.blockedVsAthleteIds.has(athlete.id)) return true;

      // No puede accionar sobre sí mismo
      if (athleteUserId && athleteUserId === currentUserId) return true;

      // Publicado, lesionado o descalificado → bloqueado
      if (status === 10 || [11, 12].includes(status)) return true;

      // Estado procesando (21): solo el juez asignado puede iniciar score.
      if (status === 21) {
        if (type === 'score') {
          return !this.isScoreAssignedToCurrentUser(athlete);
        }

        return false;
      }

      if (status === 7) return false;

      if ([17, 20].includes(status)) return true;

      // Otros estados: deshabilitar
      return true;
    },

    canViewRow(athlete) {
      const buttons = this.getVisibleButtons(athlete) || [];
      return buttons.includes('view') && !this.isDisabledButton('view', athlete);
    },

    isAssignmentBlockedForCurrentJudge(athlete) {
      if (!athlete || this.allowRecording) return false;
      if (Number(athlete?.status_id || 0) === 7) return false;
      if (!isOwnerJudgeFromCompetition(this.user, this.competitionData)) return false;
      if (isOwnerOrganizer(this.user, this.competitionData) || isSupervisor(this.user)) return false;

      const currentUserId = Number(this.user?.id || 0);
      const assignedJudgeId = Number(athlete?.judge_id || 0);

      if (!currentUserId) return false;
      return assignedJudgeId !== currentUserId;
    },

    isScoreAssignedToCurrentUser(athlete) {
      if (!athlete) return false;

      const currentUserId = Number(this.user?.id || 0);
      const assignedJudgeId = Number(athlete?.judge_id || 0);

      if (!currentUserId || !assignedJudgeId) {
        return false;
      }

      return assignedJudgeId === currentUserId;
    },

    canRespondToRowClick(athlete) {
      return this.canViewRow(athlete) || this.isAssignmentBlockedForCurrentJudge(athlete);
    },

    handleRowClick(athlete, stageName, roundNumber) {
      if (this.isAssignmentBlockedForCurrentJudge(athlete)) {
        this.$emit('assignment-blocked', {
          athlete,
          stageName,
          roundNumber,
          ...this.getTableNavigationState(),
        });
        return;
      }

      if (!this.canViewRow(athlete)) return;
      this.$emit('view-score', {
        athlete,
        stageName,
        roundNumber,
        ...this.getTableNavigationState(),
      });
    },

    isDisabled(athlete) {
      const isPending = athlete.status_id === 13;
      const isScoring = athlete.status_id === 6;
      const isRecording = athlete.status_id === 16 && !athlete.recording_end_at;
      const isScored = athlete.status_id === 7;
      const isPublished = athlete.status_id === 10;
      return isScoring || isRecording || isPending || isScored || isPublished;
    },
    emitDual(event, pair) {
      if (!pair || pair.length !== 2) return;

      this.$emit(event, pair[0]);
      this.$emit(event, pair[1]);
    },
    areBothStatus(pair) {
      const recorded = pair.every(a => a.status_id === 21);
      const completed = pair.some(a => a.status_id === 7);
      const review = pair.some(a => [19, 22].includes(a.status_id));
      const score = pair.some(a => a.status_id === 6);
      return completed || recorded || review || score;
    },
    formatScore(score) {
      const value = parseFloat(score);
      return isNaN(value) ? "0.00" : value.toFixed(2);
    },
    isDisqualifiedOrInjured(athlete) {
      return [11, 12].includes(athlete.status_id);
    },
    actionMap(action) {
      const emit = {
        start: 'start-recording',
        stop: 'stop-recording',
        score: 'score',
        edit: 'edit-score',
        disqualify: 'disqualify',
        injury:'injury',
        view:'view-score'
      };
      return emit[action] || '';
    },
    getIconClass(action) {
      const icons = {
        start: 'fa-solid fa-eye text-primary',
        stop: 'fa-solid fa-stop text-warning',
        score: 'fas fa-gavel',
        edit: 'fas fa-pen-to-square',
        disqualify: 'fas fa-ban',
        injury: 'fas fa-user-injured',
        view: 'fas fa-eye'
      };
      return icons[action] || '';
    },
    handleEmit(action,athlete,stageName,roundNumber){
      if (this.hasRunningTimer && (action === 'start' || action === 'stop')) {
        return;
      }

      if (action === 'score' && !this.canCurrentUserOpenScore(athlete)) {
        this.$emit('assignment-blocked', {
          athlete,
          stageName,
          roundNumber,
          ...this.getTableNavigationState(),
        });
        return;
      }

      if (['score', 'edit', 'view'].includes(action) && this.isAssignmentBlockedForCurrentJudge(athlete)) {
        this.$emit('assignment-blocked', {
          athlete,
          stageName,
          roundNumber,
          ...this.getTableNavigationState(),
        });
        return;
      }

      const nameForEmit = this.actionMap(action);
      let arg = {}
      if (nameForEmit === 'score' || nameForEmit === 'edit-score' || nameForEmit === 'view-score'){
        arg = {
          athlete,
          stageName,
          roundNumber,
          ...this.getTableNavigationState(),
        };
      }else{
        arg = athlete;
      }

      this.$emit(nameForEmit, arg);
    },
    isSelfAthlete(athlete) {
      const currentUserId = this.user?.id;
      const athleteUserId = athlete?.user?.id;
      return currentUserId && athleteUserId && String(currentUserId) === String(athleteUserId);
    },
    visibleVsRowActions(athlete) {
      const buttons = this.getVisibleButtons(athlete) || [];
      return buttons.filter(action => !['disqualify', 'injury', 'view'].includes(action));
    },
    canCurrentUserOpenScore(athlete) {
      if (!athlete) return false;

      const buttons = this.getVisibleButtons(athlete) || [];
      if (!buttons.includes('score')) return false;
      if (this.isDisabledButton('score', athlete)) return false;

      return this.isScoreAssignedToCurrentUser(athlete);
    },
    scoreActionButtonClass(athlete) {
      return [
        'btn',
        'btn-sm',
        'btn_actions',
        'primary-btn',
        {
          'primary-btn--muted': !this.canCurrentUserOpenScore(athlete),
        },
      ];
    },
    actionButtonClass(action, athlete = null) {
      const base = ['btn', 'btn-sm', 'btn_actions'];
      if (action === 'score') {
        base.push('primary-btn');
        if (!this.canCurrentUserOpenScore(athlete)) {
          base.push('primary-btn--muted');
        }
      }
      else if (action === 'edit') base.push('primary-btn', 'alt');
      else base.push('color-gray');
      return base.join(' ');
    },
    chunkButtons(buttons, size) {
      const chunks = [];
      for (let i = 0; i < buttons.length; i += size) {
        chunks.push(buttons.slice(i, i + size));
      }
      return chunks;
    },
    isOwner(athlete) {
      return [7,11,12].includes(athlete?.status_id) && this.isOwnerJudgeFromCompetition(this.user, this.competitionData);
    },
    isValid(athlete){
      return athlete.validated_at;
    },
    isInjured(athlete) {
      return athlete?.injured_at; // supongamos que 13 es "accidentado"
    },

    isEliminated(athlete) {
      return athlete?.disqualified_at; // supongamos que 14 es "eliminado"
    },
    getErrorCount(athlete) {
      // Lógica temporal: más adelante cambias esto por la real
      return athlete.negative_feedback_count || 0;
    },
    handleResize() {
      this.screenWidth = typeof window !== 'undefined' ? window.innerWidth : this.screenWidth;
    },
    prevPage() {
      if (this.hasPrevPage) {
        const newIndex = Math.max(0, this.pageStart - this.pageSize);
        this.currentIndex = newIndex;
      }
    },
    nextPage() {
      if (this.hasNextPage) {
        const newIndex = Math.min(this.sliderAthletes.length - 1, this.pageStart + this.pageSize);
        this.currentIndex = newIndex;
      }
    },
    selectAthlete(athlete) {
      const idx = this.sliderAthletes.findIndex(a => a.id === athlete.id);
      if (idx >= 0) {
        this.currentIndex = idx;
      }
    },
    prevVsPage() {
      if (this.hasPrevVsPage) {
        const newIndex = Math.max(0, this.vsPageStart - this.vsPageSize);
        this.currentPairIndex = newIndex;
      }
    },
    nextVsPage() {
      if (this.hasNextVsPage) {
        const newIndex = Math.min(this.vsPairs.length - 1, this.vsPageStart + this.vsPageSize);
        this.currentPairIndex = newIndex;
      }
    },
    ensureValidTablePage() {
      if (this.tablePage > this.tableTotalPages) {
        this.tablePage = this.tableTotalPages;
      }
      if (this.tablePage < 1) {
        this.tablePage = 1;
      }
    },
    ensureValidTablePairPage() {
      if (this.tablePairPage > this.tablePairTotalPages) {
        this.tablePairPage = this.tablePairTotalPages;
      }
      if (this.tablePairPage < 1) {
        this.tablePairPage = 1;
      }
    },
    goToPreviousTablePage() {
      if (this.isVsMode) {
        if (this.tablePairPage <= 1) return;
        this.tablePairPage -= 1;
        return;
      }

      if (this.tablePage <= 1) return;
      this.tablePage -= 1;
    },
    goToNextTablePage() {
      if (this.isVsMode) {
        if (this.tablePairPage >= this.tablePairTotalPages) return;
        this.tablePairPage += 1;
        return;
      }

      if (this.tablePage >= this.tableTotalPages) return;
      this.tablePage += 1;
    },
    pairTableKey(pair, index) {
      const fallback = `${this.tablePairPageStart + index}`;
      return [pair?.[0]?.id, pair?.[1]?.id].filter(Boolean).join('-') || fallback;
    },
    vsStartDisabled(pair) {
      if (!pair) return true;
      const statusBlocked = [17, 20, 21].includes(pair.left?.status_id) || [17, 20, 21].includes(pair.right?.status_id);
      return (!this.cameraReady && this.allowRecording) || this.recordingLocked || statusBlocked;
    },
    vsStopDisabled(pair) {
      if (!pair) return true;
      const isRecording = pair.left?.status_id === 16 || pair.right?.status_id === 16;
      return !isRecording;
    },
    isPairRecording(pair) {
      if (!pair) return false;
      return pair.left?.status_id === 16 || pair.right?.status_id === 16;
    },
    getPairUploadProgress(pair) {
      if (!pair) return null;
      const left = this.getUploadProgress(pair.left || {});
      const right = this.getUploadProgress(pair.right || {});
      return left || right || null;
    },
    uploadPairHasError(pair) {
      const progress = this.getPairUploadProgress(pair);
      return progress?.status === 'error';
    },
    handleVsStart(pair) {
      if (this.recordingLocked || this.hasRunningTimer) return;
      if (!pair) return;
      this.$emit('start-recording', { pair: [pair.left, pair.right].filter(Boolean) });
    },
    handleVsStop(pair) {
      if (this.hasRunningTimer) return;
      if (!pair) return;
      this.$emit('stop-recording', { pair: [pair.left, pair.right].filter(Boolean) });
    },
    handleVsUpload(pair) {
      if (this.recordingLocked) return;
      if (!pair) return;
      this.$emit('upload-from-card', { pair: [pair.left, pair.right].filter(Boolean) });
    },
    handleVsPreview(pair) {
      if (this.recordingLocked) return;
      if (!pair) return;
      this.$emit('preview-from-card', { pair: [pair.left, pair.right].filter(Boolean) });
    },
    handleVsDelete(pair) {
      if (this.recordingLocked) return;
      if (!pair) return;
      this.$emit('delete-from-card', { pair: [pair.left, pair.right].filter(Boolean) });
    }
  }
};
</script>

<style scoped>
/* Tabla */
.judge-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  border-radius: 8px;
  overflow: hidden;
  table-layout: auto;
}

.judge-table-container {
  width: 100%;
  overflow: visible;
}

.table-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
}

.table-pagination-status {
  font-size: 0.9rem;
  font-weight: 600;
  color: #4b5563;
}

.judge-table th,
.judge-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #ddd;
  text-align: left;
  font-size: 13px;
}

.judge-table tr:nth-child(even) {
  background-color: #f9f9f9;
}

/* Imagen */
.athlete-img {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 2px solid #ddd;
  object-fit: cover;
}
.status-pending    { text-shadow: 0 0 4px gray;   color: #000; }
.status-scoring    { text-shadow: 0 0 4px blue;   color: #000; }
.status-disqualified { text-shadow: 0 0 4px red;   color: #000; }
.status-injured    { text-shadow: 0 0 4px orange; color: #000; }
.status-completed  { text-shadow: 0 0 4px green;  color: #000; }
.status-recording  { text-shadow: 0 0 6px #bd0f3f; color: #000; }
.status-recorded   { text-shadow: 0 0 6px #8db70d; color: #000; }
.status-published   { text-shadow: 0 0 6px #980db7; color: #000; }
.status-review   { text-shadow: 0 0 4px blue;   color: #000;  }

/* Cabecera: Número + Imagen */
.athlete-header-cell {
  vertical-align: middle;
}

.athlete-header {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
}

.athlete-header .number {
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Nombre */

.athlete-name {
  display: flex;
  flex-direction: column;
}

.athlete-name-text {
  display: inline-block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Acciones */
.actions {
  text-align: center;
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: center;
}

.score-header {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  max-width: 160px;
  white-space: nowrap;
}

.score-label {
  display: inline-block;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-label {
  display: inline-block;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.primary-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  align-items: center;
}

.primary-btn {
  min-width: 92px;
  background: #0d6efd;
  border-color: #0b5ed7;
  color: #fff;
  padding: 9px 12px;
  min-height: 44px;
}

.primary-btn--muted {
  background: #e5e7eb;
  border-color: #cbd5e1;
  color: #475569;
}

.primary-btn--muted:hover,
.primary-btn--muted:focus {
  background: #dbe1ea;
  border-color: #cbd5e1;
  color: #334155;
}

.primary-btn--muted:disabled {
  background: #e5e7eb;
  border-color: #cbd5e1;
  color: #94a3b8;
}

.primary-btn.alt {
  background: #fbbf24;
  border-color: #d97706;
  color: #1f2937;
}

.primary-btn .btn-label {
  margin-left: 6px;
  font-weight: 600;
}

.ghost-btn {
  background: #f8fafc;
  border-color: #e2e8f0;
  color: #475569;
}

.minor-actions {
  text-align: center;
  vertical-align: middle;
}

.minor-actions-col {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
}

.tiny-btn {
  width: 32px;
  height: 32px;
  padding: 4px;
}

.button-row {
  display: flex;
  gap: 6px;
  justify-content: center;
}


.btn_actions {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  font-size: 13px;
  border: 1px solid #5b5b5b;
  border-radius: 4px;
}

.btn_actions:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn_actions:disabled i {
  color: gray !important;
}

.btn_actions.color-gray {
  color: #5b5b5b;
}

.btn_actions.delete-btn {
  border-color: #dc2626;
  color: #dc2626;
}

.btn_actions.delete-btn i {
  color: #dc2626;
}

.progress-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e0f2fe;
  color: #0f172a;
  font-weight: 700;
  font-size: 12px;
  line-height: 1;
  border: 2px solid #bae6fd;
}

.progress-circle--done {
  background: #d1fae5;
  color: #065f46;
  border: 2px solid #10b981;
}

.upload-feedback {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.35;
  text-align: center;
  width: 100%;
  max-width: 100%;
  padding: 6px 8px;
  border-radius: 8px;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
  box-sizing: border-box;
}

.upload-feedback--error {
  color: #b91c1c;
  background: rgba(185, 28, 28, 0.14);
}

.upload-feedback--info {
  color: #1d4ed8;
  background: rgba(29, 78, 216, 0.14);
}

/* Estado */
.status {
  text-align: center;
}
.score {
  text-align: center;
}
/* Solo visible en móvil */
.toggle-score-mobile {
  display: none;
  background: none;
  border: none;
  font-size: 1rem;
  margin-left: 6px;
  color: #444;
}

/* Slider de grabación */
.record-slider-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
}

.record-slider {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.record-slider-vs .record-cards-vs {
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.record-slider-placeholder {
  width: 100%;
  text-align: center;
  padding: 12px;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #475569;
  background: #f8fafc;
  font-size: 14px;
}

.record-cards {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
}

.record-cards-vs {
  display: grid;
  gap: 10px;
}

@media (max-width: 768px) {
  .record-cards {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

.record-card {
  background: #111;
  color: #fff;
  border-radius: 10px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  box-shadow: 0 6px 18px rgba(0,0,0,0.28);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.record-card.active {
  outline: 2px solid #4c6fff;
  transform: translateY(-2px);
}

.pair-card {
  padding: 12px 12px 14px;
}

.vs-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
}

.vs-separator {
  font-weight: 800;
  color: #fbbf24;
  letter-spacing: 1px;
}

.vs-athlete {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.vs-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  align-items: center;
}

.vs-upload {
  display: flex;
  align-items: center;
  justify-content: center;
}

.record-card.is-recording {
  outline: 3px solid #dc3545;
  box-shadow: 0 0 18px rgba(220, 53, 69, 0.7), 0 0 32px rgba(220, 53, 69, 0.35);
  transform: translateY(-3px);
}

.record-avatar {
  width: 80px;
  height: 80px;
  border-radius: 10px;
  object-fit: cover;
  border: 2px solid #444;
  box-shadow: 0 0 6px rgba(0,0,0,0.25);
}

.record-name {
  margin: 8px 0 6px;
  text-align: center;
  font-size: 13px;
}

.tooltip-wrapper {
  display: inline-block;
}

.upload-btn {
  background: #fff;
  border: 1px solid #e5e7eb;
  color: #0d6efd;
  min-width: 32px;
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
}

.upload-btn i {
  color: inherit;
}

.upload-btn + .upload-btn {
  margin-left: 6px;
}
.resume-btn {
  background: #0d6efd;
  border-color: #0b5ed7;
  color: #fff;
  box-shadow: 0 4px 10px rgba(13, 110, 253, 0.22);
}

.status21-btn {
  background: #eef3ff;
  border-color: #6c63ff;
  color: #3730a3;
}

.status21-btn:hover:not(:disabled) {
  background: #e3e9ff;
}

.resume-btn:hover:not(:disabled) {
  background: #0b5ed7;
}
.slider-disabled {
  opacity: 0.55;
  pointer-events: none;
}

.record-name.mini {
  font-size: 12px;
  line-height: 1.2;
  width: 100%;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.record-line {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.record-actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.record-actions.compact {
  flex-direction: row;
  justify-content: center;
}

.record-btn {
  border: none;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 44px;
}

.record-btn.start {
  background: linear-gradient(135deg, #e63946, #b81324);
}

.record-btn.stop {
  background: linear-gradient(135deg, #fbbf24, #b45309);
  color: #1f1300;
}

.nav-arrow {
  background: none;
  border: 1px solid #ccc;
  border-radius: 50%;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #333;
}

.nav-arrow:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.error-badge {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: white;
  font-size: 0.8rem;
  font-weight: bold;
}

.check-wrapper {
  display: inline-block;
  position: relative;
}

.row-clickable {
  cursor: pointer;
}

.row-clickable:hover {
  background-color: #f6f8fb;
}


/* MOBILE RESPONSIVE */
@media (max-width: 576px) {
  .judge-table,
  .judge-table thead,
  .judge-table tbody,
  .judge-table th,
  .judge-table td,
  .judge-table tr {
    display: block;
    width: 100%;
  }

  .judge-table thead {
    display: none;
  }

  .judge-table tr {
    margin-bottom: 1rem;
    border: 1px solid #ddd;
    padding: 10px;
    border-radius: 8px;
    background-color: #f9f9f9;
  }

  .judge-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: none;
  }

  .judge-table td::before {
    content: attr(data-label);
    font-weight: bold;
    flex: 1;
    padding-right: 10px;
    color: #555;
    text-align: left;
  }

  .athlete-header {
    justify-content: space-between;
    width: 100%;
  }

  .athlete-name {
    text-align: left;
  }

  .athlete-name-text {
    max-width: 140px;
  }

  .action-buttons {
    justify-content: flex-start;
  }

  .primary-actions {
    justify-content: flex-start;
  }

  .status {
    text-align: left;
  }

  .score {
    text-align: left;
  }

  .btn_actions {
    padding: 4px 6px;
    font-size: 13px;
  }

  /* Mostrar los botones de acciones menores en fila en móvil */
  .minor-actions-col {
    flex-direction: row;
    gap: 8px;
  }

  .athlete-header .number {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid #000;
    background-color: #f2f2f2;
  }
  .toggle-score-mobile {
    display: inline-block;
    vertical-align: middle;
  }

  .score-cell {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .score-cell::before {
    content: attr(data-label);
    font-weight: bold;
    flex: 1;
    color: #555;
  }

  .score-span {
    flex-shrink: 0;
    margin-left: auto;
    padding-left: 10px;
  }

  .table-pagination {
    justify-content: space-between;
    gap: 8px;
  }

  .table-pagination-status {
    font-size: 0.85rem;
  }
}

</style>